import React from 'react';
import { useTranslation } from 'react-i18next';

import { TButtonSizes } from '@private/components';

import Button from 'components/Button';
import Popover from 'components/Popover';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';

import { ActionButton, ActionsList, Container, IconButton } from './styled';

export interface IActionsConfig {
  available: boolean;
  disabled?: boolean;
  icon?: React.ReactElement;
  iconSize?: number;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  testIds?: Partial<{
    container: string;
  }>;
  text?: string;
  tooltipText?: string;
  variant?: string;
}

interface IGridActionsProps {
  actionsCount: number;
  buttonIconSize?: number;
  buttonSize?: TButtonSizes;
  className?: string;
  config: IActionsConfig[];
  icon?: React.ReactElement;
  popoverDisplayLimit?: number;
  text?: string;
  variant?: string;
}

const gridActionsTestIds = {
  container: 'gridActionsButton',
};

const GridActions = ({
  actionsCount,
  buttonIconSize = 18,
  buttonSize = 'sm',
  className,
  config,
  icon = <ICONS_MAP.More />,
  popoverDisplayLimit = 3,
  text,
  variant = 'icon',
}: IGridActionsProps) => {
  const { t } = useTranslation();

  if (!actionsCount) {
    return null;
  }

  const actionsList = (
    <ActionsList>
      {config.map(
        (
          {
            available,
            disabled,
            icon,
            iconSize = 18,
            onClick,
            testIds,
            text,
            tooltipText,
            variant = 'transparentPrimary',
          },
          index,
        ) =>
          available && (
            <ActionButton
              closeTooltipIn={0}
              disabled={disabled}
              iconSize={iconSize}
              key={index}
              onClick={onClick}
              showTooltipOnClick={false}
              startIcon={icon}
              testIds={testIds}
              tooltipText={tooltipText}
              variant={variant}
            >
              {t(text as never)}
            </ActionButton>
          ),
      )}
    </ActionsList>
  );

  const renderContent = () => {
    if (actionsCount >= popoverDisplayLimit) {
      return (
        <Popover
          component={actionsList}
          disableInteractive
          immediateAnimation
          placement='bottom-end'
        >
          <Button
            enableShowFocus
            iconSize={buttonIconSize}
            size={buttonSize}
            startIcon={icon}
            testIds={gridActionsTestIds}
            variant={variant}
          >
            {text}
          </Button>
        </Popover>
      );
    }

    return config.map(
      (
        {
          available,
          disabled,
          icon,
          iconSize = 18,
          onClick,
          testIds,
          tooltipText,
          variant = 'icon',
        },
        index,
      ) =>
        available && (
          <IconButton
            disabled={disabled}
            iconSize={iconSize}
            key={index}
            onClick={onClick}
            size='sm'
            startIcon={icon}
            testIds={testIds}
            tooltipText={tooltipText}
            variant={variant}
          />
        ),
    );
  };

  return <Container className={className}>{renderContent()}</Container>;
};

export default appReactMemo(GridActions);
